import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, ProsCons, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function ZaptecGoVsGo2({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad er forskellen på Zaptec Go og Zaptec Go 2?', answer: 'Begge er kompakte 22 kW ladebokse med Type 2-stik. Zaptec Go 2 er den nyere model med indbygget skærm, MID-certificeret måler, ISO 15118-understøttelse og forberedelse til V2G (tovejsopladning). Zaptec Go er den klassiske model med Wi-Fi + 4G og load balancing, og er billigere.' },
    { question: 'Er Zaptec Go 2 V2G-klar?', answer: 'Ja. Zaptec Go 2 er forberedt til V2G, så bilen på sigt kan sende strøm tilbage til nettet, når dansk lovgivning og elselskabsaftaler er på plads (ventes 2026–2027). Zaptec har desuden antydet, at en firmware-opdatering til den nuværende Go kan komme.' },
    { question: 'Hvilken Zaptec skal jeg vælge?', answer: 'Vil du have den billigste pålidelige løsning, så vælg Zaptec Go (4.499 kr.). Vil du fremtidssikre med V2G, indbygget skærm og MID-måler, så vælg Zaptec Go 2 (5.999 kr.). Begge inkluderer Saving Mode og serviceaftale til 0 kr./md. hos enkel energi.' },
    { question: 'Hvad koster Zaptec Go og Go 2 hos enkel?', answer: 'Zaptec Go koster 4.499 kr. og Zaptec Go 2 koster 5.999 kr. Begge priser inkluderer Saving Mode, serviceaftale og 10 øre/kWh laderabat til 0 kr./md. Installation kommer oveni fra 1.300 kr.' },
    { question: 'Passer begdge bokse til min elbil?', answer: 'Ja. Både Zaptec Go og Go 2 bruger Type 2-stik, som er standard for alle moderne elbiler i Europa – Tesla, VW, Kia, Hyundai, Polestar m.fl. Begge leverer op til 22 kW.' },
    { question: 'Er der forskel på ladehastigheden?', answer: 'Nej. Begge bokse leverer op til 22 kW (3-faset). Den faktiske hastighed afhænger af, hvad din bil og din installation kan trække. Forskellen mellem Go og Go 2 ligger i features og fremtidssikring – ikke i grundeffekten.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'tabel', label: 'Side-om-side' },
    { id: 'go', label: 'Zaptec Go' },
    { id: 'go2', label: 'Zaptec Go 2' },
    { id: 'anbefaling', label: 'Vores anbefaling' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: 'Zaptec Go vs Go 2', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Zaptec Go vs Zaptec Go 2" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Sammenligning · Ladebokse · 2026"
        h1="Zaptec Go vs Zaptec Go 2: Hvilken skal du vælge?"
        intro="To populære ladebokse fra norske Zaptec – men hvad er forskellen, og hvilken passer til dig? Vi sammenligner pris, features, V2G og fremtidssikring side om side."
        badges={['Begge 22 kW', 'Type 2-stik', 'Saving Mode inkl.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1647500666543-2f5f1e7e0b6e?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '4.499 kr.', label: 'Zaptec Go' },
          { value: '5.999 kr.', label: 'Zaptec Go 2 (V2G)' },
          { value: '22 kW', label: 'begge modeller' },
          { value: '0 kr./md.', label: 'Saving Mode + service' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Zaptec Go eller Go 2 – kort svar">
        <AnswerBox question="Hvad er forskellen på Zaptec Go og Go 2?">
          Begge er kompakte <strong>22 kW</strong> ladebokse med Type 2-stik. <strong>Zaptec Go 2</strong> er den nyere model med indbygget skærm,
          MID-certificeret måler, ISO 15118 og <strong>forberedelse til V2G</strong>. <strong>Zaptec Go</strong> er den klassiske, billigere model
          med Wi-Fi + 4G og load balancing. Vælg Go til den laveste pris, eller Go 2 til maksimal fremtidssikring.
        </AnswerBox>
      </ProseSection>

      <ProseSection id="tabel" heading="Zaptec Go vs Go 2 – side om side" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Sammenligning af Zaptec Go og Zaptec Go 2"
            columns={[{ name: 'Zaptec Go' }, { name: 'Zaptec Go 2', highlight: true }]}
            rows={[
              { label: 'Pris', values: ['4.499 kr.', '5.999 kr.'] },
              { label: 'Ladekraft', values: ['22 kW', '22 kW'] },
              { label: 'Stiktype', values: ['Type 2', 'Type 2'] },
              { label: 'Forbindelse', values: ['Wi-Fi + 4G', 'Wi-Fi + 4G'] },
              { label: 'Indbygget skærm', values: [false, true] },
              { label: 'MID-certificeret måler', values: [false, true] },
              { label: 'ISO 15118', values: [false, true] },
              { label: 'V2G-ready', values: [false, true] },
              { label: 'Load balancing', values: [true, true] },
              { label: 'Saving Mode', values: [true, true] },
              { label: 'Serviceaftale', values: ['0 kr./md.', '0 kr./md.'] },
            ]}
          />
        </div>
      </ProseSection>

      <div className="container mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div id="go">
            <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go} note="Klassikeren · bedste pris" />
            <div className="mt-4">
              <ProsCons
                pros={['Lavere pris (4.499 kr.)', 'Bevist pålidelig – Nordens mest populære', 'Wi-Fi + 4G og load balancing', 'Saving Mode + service inkluderet']}
                cons={['Ingen indbygget skærm', 'Ikke V2G-ready', 'Ingen MID-måler']}
              />
            </div>
          </div>
          <div id="go2">
            <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go_2} note="Nyeste · fremtidssikret" />
            <div className="mt-4">
              <ProsCons
                pros={['V2G-ready – fremtidssikret', 'Indbygget skærm + MID-måler', 'ISO 15118 (Plug & Charge-klar)', '5 års garanti · Saving Mode inkl.']}
                cons={['Højere pris (5.999 kr.)', 'V2G kræver fremtidig lovgivning', 'Overkill hvis du kun vil lade simpelt']}
              />
            </div>
          </div>
        </div>
      </div>

      <ProseSection id="anbefaling" heading="Vores anbefaling" alt>
        <FactGrid columns={2} facts={[
          { value: 'Vælg Go', label: 'Hvis budget vejer tungest', sub: 'Pålidelig 22 kW til laveste pris' },
          { value: 'Vælg Go 2', label: 'Hvis du vil fremtidssikre', sub: 'V2G, skærm, MID-måler' },
        ]} />
        <p className="mt-6">
          Forskellen i pris er beskeden, men forskellen i potentiale over boksens levetid kan være betydelig – især hvis du forventer at udnytte
          V2G, når det kommer til Danmark (ventes 2026–2027). Begge bokse virker med alle elbiler og inkluderer Saving Mode, serviceaftale og
          laderabat til 0 kr./md. hos enkel energi.
        </p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/zaptec-go', label: 'Zaptec Go – produktside' },
        { path: '/zaptec-go-2', label: 'Zaptec Go 2 – produktside' },
        { path: '/sammenlign/ladeboks-v2g-ready', label: 'V2G-ready ladebokse' },
        { path: '/sammenlign/v2g-guide', label: 'V2G-guide' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Vælg din Zaptec" subtext="Begge inkluderer Saving Mode, serviceaftale og laderabat til 0 kr./md." primary={{ path: '/opladere', text: 'Se ladebokse' }} secondary={{ path: '/zaptec-go-2', text: 'Se Zaptec Go 2' }} />
      <SEODisclaimer page={page} />
    </article>
  );
}